// =============================================================================
// FILE: components/branding/BottomStatusBar.tsx
// PURPOSE: Professional engineering platform status bar — fixed at viewport bottom.
//   Left:  RCD² VERIFIED certification badge with today's date.
//   Center: Active R2 module indicator.
//   Right:  Live clock + session ping indicator.
//   Height: 26px — deliberately minimal, like an IDE status bar.
// =============================================================================

import { useEffect, useState, type JSX } from 'react';
import { ShieldCheck } from 'lucide-react';

const ACCENT   = '#0d9488';
const ACCENT_DIM = 'rgba(13,148,136,0.55)';

function useLiveClock(): string {
  const fmt = () => {
    const now = new Date();
    return now.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' }) +
      ' · ' +
      now.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  };
  const [val, setVal] = useState(fmt);
  useEffect(() => {
    const id = setInterval(() => setVal(fmt()), 10_000);
    return () => clearInterval(id);
  }, []);
  return val;
}

function useCertDate(): string {
  return new Date().toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' });
}

interface BottomStatusBarProps {
  moduleName?: string;
}

export function BottomStatusBar({ moduleName }: BottomStatusBarProps): JSX.Element {
  const clock    = useLiveClock();
  const certDate = useCertDate();
  const [ping, setPing] = useState(true);

  // Slow pulse every 4s
  useEffect(() => {
    const id = setInterval(() => setPing(v => !v), 4000);
    return () => clearInterval(id);
  }, []);

  return (
    <div
      data-testid="bottom-status-bar"
      style={{
        position: 'fixed', bottom: 0, left: 0, right: 0,
        height: 26, zIndex: 9000,
        background: 'rgba(3,7,16,0.94)',
        borderTop: `1px solid ${ACCENT_DIM}`,
        backdropFilter: 'blur(10px)',
        display: 'flex', alignItems: 'center',
        padding: '0 12px',
        fontSize: 10, fontFamily: 'Arial, sans-serif',
        userSelect: 'none',
        gap: 6,
      }}
    >
      {/* Left — RCD² VERIFIED certification */}
      <div
        data-testid="status-bar-certification"
        title="RCD² Verified — AI verified for engineering quality, consistency, document integrity, and project compliance. Not a substitute for the Engineer of Record professional approval."
        style={{
          display: 'flex', alignItems: 'center', gap: 5, cursor: 'help',
          padding: '0 8px 0 0', borderRight: `1px solid ${ACCENT_DIM}`,
        }}
      >
        <ShieldCheck size={11} style={{ color: ACCENT, flexShrink: 0 }} />
        <span style={{ color: ACCENT, fontWeight: 700, letterSpacing: '0.08em', fontSize: 10 }}>
          RCD² VERIFIED
        </span>
        <span style={{ color: ACCENT_DIM, fontSize: 9 }}>
          Certified: {certDate}
        </span>
      </div>

      {/* Center — active module */}
      {moduleName && (
        <div style={{ color: 'rgba(148,172,210,0.35)', fontSize: 9, letterSpacing: '0.06em', flex: 1, textAlign: 'center' }}>
          {moduleName}
        </div>
      )}
      {!moduleName && <div style={{ flex: 1 }} />}

      {/* Right — live clock + ping */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 6, borderLeft: `1px solid rgba(148,172,210,0.12)`, paddingLeft: 8 }}>
        {/* Build version — always visible for investor demo */}
        <span
          data-testid="status-bar-version"
          style={{ color: 'rgba(245,158,11,0.5)', fontFamily: 'monospace', fontSize: 9, letterSpacing: '0.06em', borderRight: '1px solid rgba(148,172,210,0.12)', paddingRight: 6, marginRight: 2 }}
        >
          v1.0.0 · Developer Preview
        </span>
        <div
          style={{
            width: 5, height: 5, borderRadius: '50%',
            background: ping ? '#22c55e' : 'rgba(34,197,94,0.4)',
            boxShadow: ping ? '0 0 4px rgba(34,197,94,0.7)' : 'none',
            transition: 'all 1.2s ease',
            flexShrink: 0,
          }}
          title="R2 Engine online"
        />
        <span data-testid="status-bar-clock" style={{ color: 'rgba(148,172,210,0.45)', fontFamily: 'monospace', fontSize: 9, letterSpacing: '0.04em' }}>
          {clock}
        </span>
      </div>
    </div>
  );
}
